import { api } from './api';
import type { Notification, NotificationAction } from '../types';

// The signed-in user's notification feed — the notification engine's inbox and
// per-notification operations (read, unread, act), plus WhatsApp media upload.
// Uses the shared local `api` instance (src/services/api.ts).

export interface GetMyNotificationsParams {
  contactId: string;
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
  notificationType?: string;
}

export interface MyNotificationsResponse {
  data: Notification[];
  total?: number;
  unreadCount?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

// BE record-action body: { actionId, memo }
export interface RecordActionPayload {
  actionId: string;
  memo?: string;
}

export interface RecordActionResponse {
  success: boolean;
  message: string;
}

/**
 * The notification ENGINE, which is where notifications live.
 *
 * `INBOX` is the signed-in user's own inbox and takes no user parameter — the only
 * inbox you may open is your own. `RUNS` carries the per-notification operations
 * (read, act).
 */
const INBOX = '/api/v1/notification-inbox';
const RUNS = '/api/v1/notification-runs';

export const notificationsRuntimeService = {
  /** The signed-in user's inbox (BE: GET /notification-inbox).
   *
   * `contactId` is still accepted so callers need not change, but it is NOT sent:
   * the endpoint serves the caller's own inbox and nobody else's. Passing an id
   * was how the old endpoint let a client ask for someone else's messages.
   */
  getMyNotifications: async (params: GetMyNotificationsParams): Promise<MyNotificationsResponse> => {
    const response = await api.get<MyNotificationsResponse>(INBOX, {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        unreadOnly: params.unreadOnly,
      },
    });
    // The engine wraps its payload as { data: { data, total, unreadCount, ... } }.
    const body = response.data as any;
    const payload = body?.data ?? body;
    const list: Notification[] = payload.data ?? payload.notifications ?? [];
    return {
      data: list,
      total: payload.total,
      unreadCount: payload.unreadCount,
      page: payload.page,
      pageSize: payload.pageSize,
      totalPages: payload.totalPages,
    };
  },

  /** Mark a single notification as read (BE: POST /notification-runs/{id}/read). */
  markAsRead: async (notificationId: string): Promise<void> => {
    await api.post(`${RUNS}/${notificationId}/read`);
  },

  /**
   * Put an opened notification back to unread (BE: POST /notification-runs/{id}/unread).
   * Only a READ item moves — one the person already actioned stays actioned.
   */
  markAsUnread: async (notificationId: string): Promise<void> => {
    await api.post(`${RUNS}/${notificationId}/unread`);
  },

  /** Mark the caller's whole inbox read (BE: POST /notification-inbox/read-all).
   *
   * `contactId` is ignored for the same reason as above — whose inbox to clear is
   * not the client's to choose.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  markAllAsRead: async (_contactId?: string): Promise<void> => {
    await api.post(`${INBOX}/read-all`);
  },

  /** Record the recipient's response to a notification action (BE: POST /notification-runs/{id}/actions). */
  recordAction: async (
    notificationId: string,
    payload: RecordActionPayload
  ): Promise<RecordActionResponse> => {
    // The engine names it `actionCode`; the client has always called it
    // `actionId`, and the value is the same action CODE either way.
    const response = await api.post<RecordActionResponse>(
      `${RUNS}/${notificationId}/actions`,
      { actionCode: payload.actionId, memo: payload.memo }
    );
    return response.data as RecordActionResponse;
  },

  /**
   * Store a file for a WhatsApp send (BE: POST /api/v1/whatsapp/media).
   *
   * Returns the `fileId` to reference from a WhatsApp template's media, plus the
   * details the backend derived — `mediaType`, `mimeType`, `sizeBytes`. An unsendable MIME
   * type is rejected here rather than failing later at the gateway.
   *
   * `url` comes back null when the deployment has no BASE_URL configured: the
   * bytes are stored, but no gateway can fetch them, so the file is not usable
   * in a send until BASE_URL is set and the file re-uploaded.
   *
   * Requires the WHATSAPP.MEDIA permission.
   */
  uploadWhatsappMedia: async (file: File): Promise<WhatsAppMediaUpload> => {
    const form = new FormData();
    form.append('file', file);
    // The header MUST be set per-request: the shared instance defaults every call to
    // 'application/json', which overrides the multipart boundary axios would compute
    // and makes the body unparseable — the server then reports "file: Field required".
    // Same reason uploadAttachment() and fileUploadService pass it explicitly.
    const res = await api.post('/api/v1/whatsapp/media', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return (res.data?.data ?? res.data) as WhatsAppMediaUpload;
  },
};

/** The four kinds WhatsApp understands. The backend derives this from the MIME
 *  type at upload, so callers never classify a file by hand. */
export type WhatsAppMediaType = 'image' | 'document' | 'audio' | 'video';

/** What POST /api/v1/whatsapp/media returns for one stored file. */
export interface WhatsAppMediaUpload {
  fileId: string;
  url: string | null;                  // null when BASE_URL is unset — unusable for a send
  filename: string;
  mimeType: string;
  mediaType: WhatsAppMediaType;
  sizeBytes: number;
  storage: string;                     // azure | gridfs
}

export type { Notification, NotificationAction };
export default notificationsRuntimeService;
